import * as angular from 'angular';
import * as moment from 'moment';

import { ComponentDefinition, Group, IApplicationConfiguration, IApprovalButtonsConfiguration } from '../../../models';
import { ManualInputsWidget } from '../manualInputs.widget';
import { IManualInputsService, ILoggerService } from '../../../services';
import { ManualInputWidgetProperties } from '../extraProperties';
import { momentFormat } from '../../../common';

class ManualInputsPanelController implements angular.IComponentController {
    isApprovalAvailable: boolean;
    isLoading: boolean;
    // TODO should be readonly but modified in code
    save?: () => void;
    firstDayOfCalendarWeek?: number;

    readonly alwaysEdit: boolean;
    readonly application: IApplicationConfiguration;
    readonly approvedLabel: ({ item }) => string;
    readonly cancelApproval?: (groupGuid: any) => void;
    readonly dateKeyList: string[];
    readonly duplicateWidget: ({ guid }) => void;
    readonly group: Group;
    readonly groupApprovalConfig: IApprovalButtonsConfiguration;
    readonly hasApprobation?: boolean;
    readonly hasApprovalStrategy?: boolean;
    readonly isApprovalToSave: boolean;
    readonly isApproved: ({ item }) => boolean;
    readonly isEditMode: boolean;
    readonly item: ManualInputsWidget & ManualInputWidgetProperties;
    readonly loadingMessage: ({ item }) => string;
    readonly manualInputsWidgetType = 'ManualInputs';
    readonly onApprovalLinkClick: ({ item }) => void;
    readonly readManualInputs: () => angular.IPromise<any>;
    readonly selectedDate: Date;
    readonly selectedGroup: Group;
    readonly updateDate: () => angular.IPromise<any>;

    static $inject = ['$scope', 'manualInputsService', 'loggerService'];

    constructor(
        private _$scope: angular.IScope,
        private _manualInputsService: IManualInputsService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        const item = this.item;
        this.item.ctrl = {
            cancel: () => this.cancel(item),
            save: () => this.saveManualInputs(),
            isDirty: false,
        };

        // set default save method as fallback if not supplied
        if (!this.save) {
            this.save = () => this.defaultSubmitManualInputs(this.item);
        }

        this._$scope.$watch(() => this.item.configuration.enableApproval, () => {
            if (this.item && this.item.configuration) {
                this.isApprovalAvailable = this._manualInputsService
                    .isApprovalAvailable(this.item.configuration.enableApproval, this.selectedGroup, this.group);
            }
        });

        if (this.application) {
            this.updateApplication();
        }
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (this.item && this.item.ctrl && this.item.ctrl.form) {
            if (this.alwaysEdit) {
                this.item.ctrl.form.$show();
            }
        }
        if (changes['selectedGroup']?.currentValue) {
            const selectedGroup = changes['group']?.currentValue;
            if (selectedGroup && this.group && this.item && this.item.configuration && this.item.configuration.enableApproval) {
                this.isApprovalAvailable = this._manualInputsService.isApprovalAvailable(this.item.configuration.enableApproval, selectedGroup, this.group);
            }
        }
        if (changes['group']?.currentValue) {
            const group = changes['group'].currentValue;
            if (group && this.selectedGroup && this.item && this.item.configuration && this.item.configuration.enableApproval) {
                this.isApprovalAvailable = this._manualInputsService.isApprovalAvailable(this.item.configuration.enableApproval, this.selectedGroup, group);
            }
        }

        if (changes['application']?.currentValue) {
            this.updateApplication();
        }
    }

    private updateApplication() {
        this.firstDayOfCalendarWeek = this.application.firstDayOfCalendarWeek ?? this.application.weekBeginDay;
    }

    private defaultSubmitManualInputs(item: ManualInputsWidget) {
        item.ctrl.form.$submit();
    }

    saveManualInputs(): angular.IPromise<any> {
        const item = this.item;
        const { inputList } = item;
        this.isLoading = true;
        const asyncTask = !!item.configuration.launchTask;
        return this._manualInputsService
            .save(inputList, asyncTask, item.configuration.taskOptionsOverride)
            .then(count => {
                item.ctrl.form.$cancel(); //WHY???
                item.ctrl.form.$setPristine();
                this.confirmSuccess(count, asyncTask);
                return [this.readManualInputs(), this.updateDate()];
            })
            .catch(() => {
                item.ctrl.form.$show();
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    cancel(item: ManualInputsWidget & ManualInputWidgetProperties) {
        item.ctrl.form.$cancel();
        item.ctrl.form.$setPristine();
        item.inputList = angular.copy(item._pristine);
        if (this.cancelApproval) {
            this.cancelApproval({ groupGuid: this.group.guid });
        }
    }

    hasWarning(item) {
        if (item.type === this.manualInputsWidgetType) {
            return item.isExceeded || item.isInvalidApprovalConfig;
        }
        return false;
    }

    onLoadingMessage() {
        if (this.loadingMessage) {
            this.loadingMessage({ item: this.item });
        }
    }

    onDuplicateWidget() {
        if (this.duplicateWidget) {
            this.duplicateWidget({ guid: this.item.guid });
        }
    }

    onIsApproved() {
        if (this.isApproved) {
            return this.isApproved({ item: this.item });
        }
        return false;
    }

    onApprovedLabel() {
        if (this.approvedLabel) {
            return this.approvedLabel({ item: this.item });
        }
        return '';
    }

    getDateKey(date: Date): string {
        return moment(date).format(momentFormat);
    }

    private confirmSuccess(count: number, taskFeedback: boolean): void {
        if (taskFeedback && count > 0) {
            this._loggerService.logSuccess(`${count} new reprocessing tasks launched.`, true);
        }
        this._loggerService.logSuccess('Successfully saved manual inputs.', true);
    }
}

export const wtManualInputsPanel: ComponentDefinition = {
    componentId: 'wtManualInputsPanel',
    controller: ManualInputsPanelController,
    template: require('./manualInputsPanel.component.html'),
    controllerAs: 'vm',
    bindings: {
        alwaysEdit: '<?',
        application: '<',
        approvedLabel: '&',
        cancelApproval: '&?',
        dateKeyList: '<?',
        duplicateWidget: '&',
        group: '<',
        groupApprovalConfig: '<?',
        hasApprobation: '<',
        hasApprovalStrategy: '<?',
        hasManualInputRole: '<?',
        isApprovalToSave: '<',
        isApproved: '&',
        isEditMode: '<',
        item: '<',
        loadingMessage: '&',
        onApprovalLinkClick: '&?',
        readManualInputs: '&',
        save: '&?',
        selectedDate: '<',
        selectedGroup: '<',
        showButtons: '<?',
        updateDate: '&',
    }
};